/**
 * Recettes — volet de l'onglet Bibliothèque (specs §6.5, §8.2).
 *
 * ⚠️ ARBITRAGE Bibliothèque / Réglages, tranché à l'étape 5 et appliqué ici : les
 * **objets** du référentiel vivent à la Bibliothèque (§8.2), les **préférences de
 * comportement** aux Réglages (§6.8). Une recette est un objet, au même titre
 * qu'un aliment ou un modèle de journée.
 *
 * ⚠️ **Supprimer une recette ne touche pas au journal** (§4.1) : les blocs déjà
 * enregistrés gardent leur libellé, leurs macros et leurs lignes d'ingrédients.
 * L'écran le dit, parce que c'est l'inverse de ce qu'on redoute en cliquant sur
 * « Supprimer ».
 *
 * ⚠️ **Aucune macro n'est affichée comme modifiable ici.** Elles viennent du
 * serveur, calculées depuis les ingrédients (specs §6.5) — la liste les montre,
 * l'éditeur aussi, mais nulle part il n'y a de champ pour les saisir.
 *
 * ⚠️ **Refonte** : même gabarit que les aliments — recherche en pilule, liste en
 * `.list-card` —, plus la rangée de puces de tag du prototype (`.chip-row`).
 *
 * 🔴 **Les puces sont des `Button`, pas des `Badge`.** C'est la distinction
 * posée dans `Badge.tsx` : un badge est une étiquette **statique**, une puce de
 * filtre se tape. Leur donner la même apparence inviterait à tapoter les tags
 * d'une ligne de liste, qui eux ne font rien.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import type { RecipeDTO } from '../../../../shared/types';
import { readError } from '../../api/client';
import { fetchFoods, QUICK_ACCESS_KEY } from '../../api/foods';
import {
  createRecipe,
  deleteRecipe,
  fetchRecipes,
  fetchRecipeTags,
  updateRecipe,
  RECIPES_KEY,
  RECIPE_TAGS_KEY,
} from '../../api/recipes';
import { Badge, Button, Card, GlyphSearch, GlyphStar, TextField } from '../../components/nourri';
import { ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { searchRecipesLocally } from '../../lib/localSearch';
import { newClientId } from '../../lib/dates';
import { formatMacro } from '../../lib/macros';
import { RecipeEditor } from './RecipeEditor';
import {
  draftOfRecipe,
  emptyRecipeDraft,
  inputOfRecipeDraft,
  updateOfRecipeDraft,
  type RecipeDraft,
} from './recipeDraft';

type Editing = { mode: 'create' } | { mode: 'edit'; recipe: RecipeDTO };

/** « 4 portions · 512 kcal / portion » — la ligne qui résume un rendement. */
function yieldSummary(recipe: RecipeDTO): string {
  if (recipe.yieldType === 'portions') {
    const perPortion = recipe.perPortion;
    const per = perPortion === null ? '' : ` · ${formatMacro(perPortion.kcal, 'kcal')} kcal / portion`;
    return `${formatMacro(recipe.yieldValue, 'g')} portions${per}`;
  }
  const per100 = recipe.per100;
  const per = per100 === null ? '' : ` · ${formatMacro(per100.kcal, 'kcal')} kcal / 100 g`;
  return `${formatMacro(recipe.yieldValue, 'g')} g${per}`;
}

function RecipeRow({
  recipe,
  onEdit,
  onToggleFavorite,
  onDelete,
  busy,
}: {
  recipe: RecipeDTO;
  onEdit: () => void;
  onToggleFavorite: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  return (
    <li className="n-list__row">
      <Button
        variant="ghost"
        shape="circle"
        className={recipe.isFavorite ? 'n-star' : 'n-star n-star--off'}
        aria-pressed={recipe.isFavorite}
        aria-label={
          recipe.isFavorite
            ? `Retirer ${recipe.name} des favoris`
            : `Mettre ${recipe.name} en favori`
        }
        onClick={onToggleFavorite}
      >
        <GlyphStar filled={recipe.isFavorite} />
      </Button>

      <button type="button" onClick={onEdit} className="n-list__main">
        <span className="n-list__name">{recipe.name}</span>
        <span className="n-list__meta tnum">
          {yieldSummary(recipe)} · {String(recipe.ingredients.length)} ingrédient(s)
        </span>
        {/*
          ⚠️ Les tags de la ligne sont des `Badge` — étiquettes **statiques**.
          Le filtre, lui, est la rangée de puces au-dessus de la liste : deux
          objets, deux composants, parce qu'un seul des deux se tape.
        */}
        {recipe.tags.length > 0 && (
          <span className="n-list__tags">
            {recipe.tags.map((item) => (
              <Badge key={item}>{item}</Badge>
            ))}
          </span>
        )}
      </button>

      <Button
        variant="danger"
        disabled={busy}
        onClick={onDelete}
        aria-label={`Supprimer ${recipe.name}`}
      >
        Suppr.
      </Button>
    </li>
  );
}

export function RecipesSection() {
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [draft, setDraft] = useState<RecipeDraft>(emptyRecipeDraft);
  const [clientId, setClientId] = useState(newClientId);

  /*
   * 🔴 **La réplique du §3.1 — une seule clé, ni terme ni tag dedans** (V2,
   * étape 4). Auparavant `[...RECIPES_KEY, terme, tag]` : hors ligne, une
   * recherche jamais faite en ligne n'avait rien à restaurer, la requête restait
   * en pause et l'écran attendait. Le §3.1 promet pourtant « rechercher dans
   * […] les recettes » sans réseau.
   *
   * La liste entière tient en quelques dizaines de lignes : la charger une fois
   * et la filtrer en mémoire (`lib/localSearch.ts`, mêmes primitives que le
   * serveur) coûte moins qu'un aller-retour par frappe — et marche en salle.
   */
  const recipes = useQuery({ queryKey: RECIPES_KEY, queryFn: () => fetchRecipes('') });

  const cherche = term.trim();
  const resultats = useMemo(
    () => searchRecipesLocally(recipes.data ?? [], cherche, tag),
    [recipes.data, cherche, tag],
  );
  const tags = useQuery({ queryKey: RECIPE_TAGS_KEY, queryFn: fetchRecipeTags });

  /*
   * Les fiches d'aliments servent à reconstituer un brouillon depuis une recette
   * existante (`draftOfRecipe`), qui a besoin des portions et des macros pour
   * l'aperçu. Chargées ici plutôt que dans l'éditeur : elles doivent être là au
   * moment où l'on ouvre l'éditeur, pas une frappe plus tard.
   */
  const foods = useQuery({ queryKey: ['foods', ''], queryFn: () => fetchFoods('') });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: RECIPES_KEY });
    void queryClient.invalidateQueries({ queryKey: RECIPE_TAGS_KEY });
    void queryClient.invalidateQueries({ queryKey: QUICK_ACCESS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['food-search'] });
  };

  const save = useMutation({
    mutationFn: () =>
      editing?.mode === 'edit'
        ? updateRecipe(editing.recipe.id, updateOfRecipeDraft(draft))
        : createRecipe(inputOfRecipeDraft(draft, clientId)),
    onSuccess: () => {
      refresh();
      setEditing(null);
    },
  });

  const favorite = useMutation({
    mutationFn: (recipe: RecipeDTO) => updateRecipe(recipe.id, { isFavorite: !recipe.isFavorite }),
    onSuccess: refresh,
  });

  const remove = useMutation({ mutationFn: deleteRecipe, onSuccess: refresh });

  const openCreate = () => {
    setDraft(emptyRecipeDraft());
    // Tiré une fois par ouverture : rejouer l'enregistrement — double tap,
    // reprise réseau — ne crée pas une seconde recette (règle n°9).
    setClientId(newClientId());
    setEditing({ mode: 'create' });
  };

  return (
    <div className="n-page">
      <TextField
        shape="pill"
        lead={<GlyphSearch />}
        aria-label="Chercher une recette ou un tag"
        placeholder="Chercher une recette ou un tag…"
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        selectOnFocus={false}
      />

      {/*
        Filtrage par tag (specs §6.5). Un seul tag à la fois : croiser deux tags
        demanderait une interface de requête pour un besoin qui ne se présente
        pas à cette échelle.

        ⚠️ `variant="selected"` sur la puce active — la même façon de dire
        « celui-ci » que les sous-onglets et les plages de Progression.
      */}
      {(tags.data ?? []).length > 0 && (
        <div className="n-chip-row">
          {(tags.data ?? []).map((item) => (
            <Button
              key={item}
              variant={tag === item ? 'selected' : 'secondary'}
              aria-pressed={tag === item}
              onClick={() => {
                setTag((current) => (current === item ? null : item));
              }}
            >
              {item}
            </Button>
          ))}
        </div>
      )}

      {recipes.isPending && <Hint>Chargement…</Hint>}
      <ReadNote error={readError(recipes)} quoi="la liste des recettes" />
      {save.isError && <ErrorNote>{save.error.message}</ErrorNote>}
      {remove.isError && <ErrorNote>{remove.error.message}</ErrorNote>}
      {favorite.isError && <ErrorNote>{favorite.error.message}</ErrorNote>}

      {recipes.data !== undefined && resultats.length === 0 && (
        <p className="n-empty">
          {cherche === '' && tag === null
            ? 'Aucune recette. Une recette assemble des aliments de votre base ; ses macros sont calculées toutes seules, jamais saisies.'
            : 'Aucune recette ne correspond.'}
        </p>
      )}

      {resultats.length > 0 && (
        <Card padding="none">
          <ul className="n-list">
            {resultats.map((recipe) => (
              <RecipeRow
                key={recipe.id}
                recipe={recipe}
                busy={remove.isPending}
                onEdit={() => {
                  setDraft(draftOfRecipe(recipe, foods.data ?? []));
                  setEditing({ mode: 'edit', recipe });
                }}
                onToggleFavorite={() => {
                  favorite.mutate(recipe);
                }}
                onDelete={() => {
                  remove.mutate(recipe.id);
                }}
              />
            ))}
          </ul>
        </Card>
      )}

      <Button variant="primary" size="lg" block onClick={openCreate}>
        Nouvelle recette
      </Button>

      <p className="n-foot-note">
        Supprimer une recette ne touche pas au journal : les blocs déjà enregistrés gardent leurs
        ingrédients et leurs macros, figés à la saisie.
      </p>

      {editing && (
        <RecipeEditor
          // Une clé par cible d'édition : le brouillon repart de zéro quand on
          // passe d'une recette à une autre.
          key={editing.mode === 'edit' ? `edit-${String(editing.recipe.id)}` : 'create'}
          open
          title={editing.mode === 'edit' ? `Modifier « ${editing.recipe.name} »` : 'Nouvelle recette'}
          draft={draft}
          onDraftChange={setDraft}
          saving={save.isPending}
          onSubmit={() => {
            save.mutate();
          }}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
